import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { config } from '@app/core/app-config';
import { Webhook, WebhookResponse } from '@app/shared/interfaces/lib/webhook.interface';
import { Observable, of } from 'rxjs'; // Import 'of' from 'rxjs'
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class WebhooksService {
  // Updated class name
  private webhooksUrl = `${config.API_URL}/webhooks-next`;

  constructor(private http: HttpClient) {}

  getWebhooks(): Observable<Webhook[]> {
    return this.http.get(this.webhooksUrl).pipe(
      map((res: any) => {
        return res.data.map((webhook: any) => ({
          ...webhook,
        }));
      })
    );
  }

  addWebhook(newWebhook: Webhook): Observable<WebhookResponse> {
    return this.http
      .post(this.webhooksUrl, {
        url: newWebhook.url,
        name: newWebhook.name,
        types: newWebhook.types,
      })
      .pipe(
        map((res: any) => {
          return {
            webhook: res.data
              ? {
                  ...res.data,
                }
              : null,
            error: res.error || null,
          } as WebhookResponse;
        }),
        catchError((error) => {
          return of({ webhook: null, error: 'Failed to add webhook' });
        })
      );
  }

  updateWebhookUrl(hook: Webhook): Observable<WebhookResponse> {
    return this.http.put(`${this.webhooksUrl}/${hook.id}`, { url: hook.url }).pipe(
      map((res: any) => {
        return {
          webhook: res.data
            ? {
                ...res.data,
              }
            : null,
          error: res.error || null,
        } as WebhookResponse;
      }),
      catchError((error) => {
        return of({ webhook: null, error: 'Failed to update webhook' });
      })
    );
  }

  updateWebhook(webhookId: string, updates: Partial<Webhook>): Observable<WebhookResponse> {
    return this.http.put(`${this.webhooksUrl}/${webhookId}`, updates).pipe(
      map((res: any) => {
        return {
          webhook: res.data
            ? {
                ...res.data,
              }
            : null,
          error: res.error || null,
        } as WebhookResponse;
      }),
      catchError((error) => {
        return of({ webhook: null, error: 'Failed to update webhook' });
      })
    );
  }

  updateWebhookName(hook: Webhook): Observable<WebhookResponse> {
    return this.http.put(`${this.webhooksUrl}/${hook.id}`, { name: hook.name }).pipe(
      map((res: any) => {
        return {
          webhook: res.data ?? null,
          error: res.error || null,
        } as WebhookResponse;
      }),
      catchError((error) => {
        return of({ webhook: null, error: 'Failed to update webhook' });
      })
    );
  }

  updateWebhookTypes(hook: Webhook): Observable<WebhookResponse> {
    return this.http.put(`${this.webhooksUrl}/${hook.id}`, { types: hook.types }).pipe(
      map((res: any) => {
        return {
          webhook: res.data
            ? {
                ...res.data,
              }
            : null,
          error: res.error || null,
        } as WebhookResponse;
      }),
      catchError((error) => {
        return of({ webhook: null, error: 'Failed to update webhook types' });
      })
    );
  }

  deleteWebhook(webhookId: string): Observable<boolean> {
    return this.http.delete(`${this.webhooksUrl}/${webhookId}`).pipe(map((res: any) => res.success));
  }

  testWebhook(webhookId: string): Observable<any> {
    return this.http.put(`${this.webhooksUrl}/${webhookId}/test`, {}).pipe(map((res: any) => res));
  }

  sendAllWebhooks(webhookId: string): Observable<boolean> {
    return this.http.post(`${this.webhooksUrl}/${webhookId}/send-all`, {}).pipe(map((res: any) => res.success));
  }

  sendAllLegacyWebhooks(webhookId: string): Observable<boolean> {
    return this.http.put(`${this.webhooksUrl}/send-previous`, {}).pipe(map((res: any) => res.success));
  }
}
